<template>
  <div class="d-xl-flex" :class="{'sidebar-right--hidden': !sidebar}">
    <div class="c-body">
      <main class="c-main">
        <CContainer>
          <div class="d-flex justify-content-end event-wizard-adv"
               v-if="showAdvanced">
            <div class="d-flex align-items-end justify-content-end">
              <label class="m-0">{{ $pgettext('event_template.label', 'Advanced settings') }}</label>
              <CSwitch class="ml-2 mt-2" color="primary"
                       variant="3d" size="sm"
                       :checked="advanced(step)"
                       @update:checked="setAdvanced({value:!advanced(step),type:step})"
              />
            </div>
          </div>
        </CContainer>
        <TabConfiguration
            v-if="['template','plan_settings', 'vertical','event_settings'].includes(step)"
            :step="step"
            @save="saveStep"
        />

        <StepDateAndName
            v-if="step === 'date_and_name'"
            :data="form['date_and_name']"
            :loading="isStepLoading('date_and_name')"
            :date_type="configuration.date_type"
            :enable_logo="configuration.allows_profile_event_photo"
            :enable_welcome_code="enable_welcome_code"
            :propose_welcome_code="onPicaCodeRequest"
            :allows_custom_welcome_code="configuration.allows_custom_welcome_code"
            :validate_welcome_code="validateWelcomeCode"
            :get_logo_upload_intent="(file) => getWizardUploadIntent({field:'logo',file:file})"
            :get_upload_intent_public_url="getUploadIntentPublicUrl"
            :published="!isDraft"
            :promo="isPromo"
            @save="saveStep"
            @updatePreview="updatePreviewHome"
        />

        <StepCustomizedLogoOnPhoto
            v-if="step === 'customize_logo_on_photo'"
            :data="form['customize_logo_on_photo']"
            :loading="isStepLoading('customize_logo_on_photo')"
            @save="fetchStep('customize_logo_on_photo')"
            :get_logo_upload_intent="(file) => getWizardUploadIntent({field:'copyright',file:file})"
            :event_started="configuration.event_started"
            :get_upload_intent_public_url="getUploadIntentPublicUrl"
            :advanced="advanced('customize_logo_on_photo')"
            @updatePreview="updatePreviewLogo"
        />

        <StepBannerADVAndLink
            v-if="step === 'banner_advertisement'"
            :data="this.form['banner_advertisement']"
            :loading="isStepLoading('banner_advertisement')"
            @save="saveStep"
            :get_banner_upload_intent="(file) => getWizardUploadIntent({field:'banner',file:file})"
            :get_upload_intent_public_url="getUploadIntentPublicUrl"
            @updatePreview="updatePreviewBanner"
        />

        <StepPrivacy
            v-if="step === 'privacy'"
            :data="this.form['privacy']"
            :loading="isStepLoading('privacy')"
            @save="saveStep"
            @updatePreview="updatePreviewPrivacy"
            @downloadShareData="exportSharedData"
        />

        <StepForm
            v-if="step === 'form'"
            :data="this.form['form']"
            :advanced="advanced('form')"
            :loading="isStepLoading('form')"
            @save="saveStep"
            @downloadResults="exportFormResults"
            :get_consent_upload_intent="(file) => getWizardUploadIntent({field:'consent',file:file})"
            :get_logo_upload_intent="(file) => getWizardUploadIntent({field:'form_logo',file:file})"
            :get_upload_intent_public_url="getUploadIntentPublicUrl"
            :get_form_s3_public_url="getFormS3PublicUrl"
            :is-pica-manager="isPicaManager"
            :is-published="!isDraft"
            @updatePreview="updatePreviewForm"
        />

        <StepCheckinOptions
            v-if="step === 'checkin_options'"
            :data="this.form['checkin_options']"
            :loading="isStepLoading('checkin_options')"
            @save="saveStep"
            :get_image_upload_intent="(file) => getWizardUploadIntent({field:'checkin_option_image',file:file})"
            :get_upload_intent_public_url="getUploadIntentPublicUrl"
            @updatePreview="updatePreviewCheckinOptions"
        />


        <StepEmailScheduler
            v-if="step === 'email_scheduler'"
            :data="this.form['email_scheduler']"
            :loading="isStepLoading('email_scheduler')"
            @save="saveStep"
        />

        <StepPopup
            v-if="step === 'popup'"
            :data="this.form['popup']"
            :loading="isStepLoading('popup')"
            @save="saveStep"
            @updatePreview="updatePreviewPopup"
        />

        <StepFolderMapping
            v-if="step === 'folder_mapping'"
            :data="this.form['folder_mapping']"
            :loading="isStepLoading('folder_mapping')"
            :availableTags="tags"
            @save="saveStep"
        />

        <StepPicaCodes
            v-if="step === 'pica_codes'"
            :data="this.form['pica_codes']"
            :loading="isStepLoading('pica_codes')"
            @save="fetchStep('pica_codes')"
        />

        <StepOverview
            v-if="step === 'overview'"
            :data="this.form['overview']"
            :loading="isStepLoading('overview')"
        />
      </main>
    </div>
    <div class="c-sidebar-right"  v-if="[
                      'date_and_name',
                      'customize_logo_on_photo',
                      'banner_advertisement',
                      'form',
                      'checkin_options',
                      'privacy',
                      'popup',
                      'galleries'
                    ].includes(step)">
      <PreviewBar :overflow-hidden="true"
                  @toggle="onToggleSidebar">
        <PreviewHome :data="preview.home" v-if="['date_and_name'].includes(step)"/>
        <PreviewLogo :data="preview.logo" v-else-if="['customize_logo_on_photo'].includes(step)"/>
        <PreviewBanner :data="preview.banner" v-else-if="['banner_advertisement'].includes(step)"/>
        <PreviewForm :data="preview.form" v-else-if="['form'].includes(step)"/>
        <PreviewCheckinOptions :data="preview.checkin_options" v-else-if="['checkin_options'].includes(step)"/>
        <PreviewPrivacy :data="preview.privacy" v-else-if="['privacy'].includes(step)"/>
        <PreviewPopup :data="preview.popup" v-else-if="['popup'].includes(step)"/>
      </PreviewBar>
    </div>
  </div>
</template>

<script>
import StepDateAndName from "@/domain/eventWizard/steps/StepDateAndName"
import {mapActions, mapGetters} from "vuex"
import StepCustomizedLogoOnPhoto from "@/domain/eventWizard/steps/StepCustomizedLogoOnPhoto.vue";
import StepBannerADVAndLink from "@/domain/eventWizard/steps/StepBannerADVAndLink.vue";
import StepCheckinOptions from "@/domain/eventWizard/steps/StepCheckinOptions.vue";
import StepForm from "@/domain/eventWizard/steps/StepForm.vue";
import PreviewForm from "@/domain/eventWizard/preview/PreviewForm.vue";
import PreviewBar from "@/domain/eventWizard/preview/PreviewBar.vue";
import PreviewHome from "@/domain/eventWizard/preview/PreviewHome.vue";
import PreviewLogo from "@/domain/eventWizard/preview/PreviewLogo.vue";
import PreviewBanner from "@/domain/eventWizard/preview/PreviewBanner.vue";
import PreviewCheckinOptions from "@/domain/eventWizard/preview/PreviewCheckinOptions.vue";
import StepPrivacy from "@/domain/eventWizard/steps/StepPrivacy.vue";
import PreviewPrivacy from "@/domain/eventWizard/preview/PreviewPrivacy.vue";
import StepPopup from "@/domain/eventWizard/steps/StepPopup.vue";
import PreviewPopup from "@/domain/eventWizard/preview/PreviewPopup.vue";
import StepEmailScheduler from "@/domain/eventWizard/steps/StepEmailScheduler.vue";
import StepFolderMapping from "@/domain/eventWizard/steps/StepFolderMapping.vue";
import TabConfiguration from "@/domain/eventWizard/steps/TabConfiguration.vue";
import StepPicaCodes from "@/domain/eventWizard/steps/StepPicaCodes.vue";
import StepOverview from "@/domain/eventWizard/steps/StepOverview.vue";

export default {
  name: 'EventWizard',
  components: {
    StepOverview,
    StepPicaCodes,
    TabConfiguration,
    StepFolderMapping,
    StepEmailScheduler,
    PreviewPopup,
    StepPopup,
    PreviewPrivacy,
    StepPrivacy,
    PreviewCheckinOptions,
    PreviewBanner,
    PreviewLogo,
    PreviewHome,
    PreviewBar,
    PreviewForm,
    StepForm,
    StepBannerADVAndLink,
    StepCustomizedLogoOnPhoto,
    StepDateAndName,
    StepCheckinOptions,
  },

  data() {
    return {
      sidebar: true,
      changed: {},
      preview: {
        home: {},
        logo: {},
        banner: {},
        form: {},
        privacy: {},
        checkin_options: {},
        popup: {},
      }
    }
  },
  props: {
    step: {
      type: String,
      required: true
    },
  },
  computed: {
    ...mapGetters('eventWizard', [
      'steps',
      'form',
      'actualStep',
      'configuration',
      'advanced',
      'isStepLoading',
    ]),
    ...mapGetters('event', [
      'isDraft',
      'isPromo'
    ]),
    ...mapGetters('login', [
      'isPicaManager'
    ]),
    tags() {
      return this.form?.['configuration']?.['tags'] ?? {};
    },
    enable_welcome_code() {
      // If we don't require a welcome code, this should not appear anywhere
      if (!this.configuration.requires_welcome_code) return false
      // If the user has the permission to change the welcome code after publish, we should show it even if it will be auto-generated
      if (this.hasPerm('advanced.change_welcome_code_after_publish')) return true
      if (this.configuration.auto_generate_welcome_code) return false
      return true
    },
    showAdvanced() {
      if (this.step === 'customize_logo_on_photo')
        return this.hasPerm('advanced.customize_logo_on_photo')
      if (this.step === 'form')
        return this.hasPerm('advanced.form')
      return false
    }
  },

  methods: {
    ...mapActions('eventWizard', [
      'fetchFormData',
      'setStepAsDone',
      'setNextStepAsActive',
      'updateFormData',
      'fetchFreePicaCodesForEventName',
      'getWizardUploadIntent',
      'publish',
      'setEventId',
      'fetchEventConfiguration',
      'exportFormResults',
      'exportSharedData',
      'getUploadIntentPublicUrl',
      'getFormS3PublicUrl',
      'setAdvanced',
      'validateWelcomeCode',
      'fetchStepData',
      'searchRelease',
    ]),
    ...mapActions('core', [
      'autocompleteTags'
    ]),
    updatePreviewForm(data) {
      this.preview.form = data
    },
    updatePreviewCheckinOptions(data) {
      this.preview.checkin_options = data
    },
    updatePreviewPopup(data) {
      this.preview.popup = data
    },
    async updatePreviewHome(data) {
      this.preview.home = {
        ...data,
        gallery: []
      }
    },
    async updatePreviewLogo(data) {
      this.preview.logo = data
    },
    async updatePreviewBanner(data) {
      this.preview.banner = {
        ...data,
        gallery: []
      }
    },
    async updatePreviewPrivacy(data) {
      this.preview.privacy = {
        ...data,
      }
    },

    async saveStep(data) {
      await this.updateFormData({step: this.step, data: data})
      await this.notifySuccess(this.$pgettext('event_wizard', 'Event Updated'))
    },

    async fetchStep(step) {
      await this.fetchStepData(step)
    },

    async saveConfiguration(data) {
      const step = 'configuration'
      await this.updateFormData({step: step, data})
      await this.fetchEventConfiguration()
      await this.fetchFormData()
      await this.notifySuccess(this.$pgettext('event_wizard', 'Configuration Updated'))
    },

    async onPicaCodeRequest({name, start_at, custom}) {
      return await this.fetchFreePicaCodesForEventName({name: name, date: start_at, custom: custom})
    },

    onToggleSidebar(state) {
      this.sidebar = state
    }
  }
}
</script>
